// Paging walk for list endpoints whose callers mean "all of them".
//
// The failure this exists to prevent is silent: a caller asks for a page
// larger than the server will serve, gets a truncated list with HTTP 200 and
// no warning, and the rows past the boundary simply never render. The
// property catalogue outgrew a single page (653 definitions against a 500
// maximum) and `serial_number` — rank 522 by code — disappeared from the
// asset Properties tab, from the suggestions list, and from the
// property-mapping-rule pickers, with nothing logged anywhere.
//
// Keeping the walk in one module means every "give me everything" call site
// shares one set of stop conditions instead of re-deriving them, usually
// wrongly.
import { apiClient } from './client'

// The largest page the paginated list endpoints will honour — `limit` is
// clamped to this server-side (`base.ClampLimit(raw, 20, 500)`), which now
// also sets a `Warning: 299` header when an over-ask is trimmed. Asking for
// more is therefore not an error and not something the walk can detect from
// the body, which is exactly why the page size lives here as a constant
// rather than as an optimistic literal at each call site.
export const LIST_PAGE_SIZE_MAX = 500

// The envelope every paginated list endpoint returns. `data` is `null` (not
// `[]`) for an empty list. `total_pages` is deliberately absent: most
// endpoints never emit it — the properties endpoint emits `data`, `total`,
// `page` and `limit` only — so a loop keyed on it stops after page one. The
// OpenAPI schema no longer declares it required for that reason.
interface PaginatedBody<T> {
    data?: T[] | null
    total?: number
}

// openapi-fetch narrows `response.error` to `never` for operations the
// generated types give no error response, so the pair is read through a
// structural type rather than pretending the error side is absent.
interface FetchResult<T> {
    data?: PaginatedBody<T>
    error?: unknown
}

export interface PaginateAllParams {
    /** Path parameters, e.g. `{ orgCode: org }`. */
    path: Record<string, string>
    /** Query parameters other than `page`; include `limit` yourself. */
    query?: Record<string, unknown>
}

// listQueryKey builds the query key openapi-react-query would have produced
// for this GET — `['get', path, { params }]`.
//
// This shape is load-bearing, not cosmetic: `src/lib/live-invalidation.ts`
// and the property-category mutations select cache entries with
// `key[0] === 'get' && key[1] === path`, so a bespoke key would leave the
// query permanently stale instead of erroring. Callers that page must
// therefore keep the generated shape even though they run their own
// `queryFn`.
export function listQueryKey(path: string, params: PaginateAllParams): unknown[] {
    return ['get', path, { params: { path: params.path, query: params.query } }]
}

/**
 * Fetches every page of a paginated list endpoint and returns the
 * concatenation, in server order.
 *
 * Two independent stop conditions, because neither alone is safe:
 *
 * - **a short page** (`batch.length < pageSize`) ends the walk even when the
 *   envelope carries no count — and it often does not;
 * - **`fetched >= total`** ends it on the exact boundary where the last page
 *   happens to be full, sparing one empty round-trip.
 *
 * Never `total_pages`: see `PaginatedBody` above.
 */
export async function fetchAllPages<T>(
    endpoint: string,
    params: PaginateAllParams,
    pageSize: number = LIST_PAGE_SIZE_MAX,
): Promise<T[]> {
    const all: T[] = []
    const query = { ...params.query, limit: pageSize }

    for (let page = 1; ; page++) {
        const response = await apiClient.GET(
            // The endpoint is a runtime string here; openapi-fetch's per-path
            // typing cannot follow it, and the response shape is asserted by
            // FetchResult<T> instead.
            endpoint as never,
            { params: { path: params.path, query: { ...query, page } } } as never,
        ) as FetchResult<T>

        if (response.error) throw response.error

        const body = response.data
        // The API represents an empty list as `data: null`, not `data: []`.
        const batch = body?.data ?? []
        all.push(...batch)

        if (batch.length < pageSize) break
        if (typeof body?.total === 'number' && all.length >= body.total) break
    }

    return all
}
